import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { IconBullet } from "@/pages/UserGoOn/Desks/ResumoDesk/Icons";
import { ExpandableText } from "@/pages/UserGoOn/Desks/ResumoDesk/Timeline/ExpandableText";
import { FileIcon } from "lucide-react";
import {
  getTimelineItemDepartamentoId,
  parseTimelineSnapshot,
} from "@/utils/timelineDepartamento";

type Departamento = {
  id: number | string;
  nome: string;
};

type TimelineNotaSnapshot = {
  id_departamento?: number | string;
  title?: string;
  description?: string;
  [key: string]: unknown;
};

type TimelineNotaData = {
  alteracao?: string;
  old?: string | null;
  new?: string | null;
  createdAt: string | number | Date;
  nota?: {
    id_departamento?: number | string;
    title?: string;
    description?: string;
    file_path?: string;
  };
  user?: {
    name?: string;
    last_name?: string;
    url_img?: string;
  };
};

type Mudanca = {
  antigo: unknown;
  novo: unknown;
  Status_Desk: "Diferentes";
};

export function TimelineNotas({
  data,
  departamentos,
}: {
  data: TimelineNotaData;
  departamentos: Departamento[];
  departamentoActive?: unknown;
}) {
  const { t } = useTranslation();
  const parsedNew = parseTimelineSnapshot<TimelineNotaSnapshot>(data.new);
  const departamentoId = getTimelineItemDepartamentoId(data);

  function normalizeValue(value: unknown): string {
    if (value === null || value === undefined || value === "null") return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }

  function areValuesDifferent(oldVal: unknown, newVal: unknown): boolean {
    return normalizeValue(oldVal) !== normalizeValue(newVal);
  }

  function encontrarMudancas(objeto: TimelineNotaData) {
    if (!objeto.old || !objeto.new) return {};

    const oldObj = parseTimelineSnapshot<TimelineNotaSnapshot>(objeto.old);
    const newObj = parseTimelineSnapshot<TimelineNotaSnapshot>(objeto.new);
    if (!oldObj || !newObj) return {};

    const mudancas: Record<string, Mudanca> = {};

    const nomePropriedades: Record<string, string> = {
      date_end: "Data Final",
      date_init: "Data Inicial",
      id_responsavel: "Responsável",
      status: "Status",
      title: "Título",
      date: "Data",
      description: "Descrição",
      id_departamento: "Departamento",
    };

    const oldCopy = { ...oldObj };
    const newCopy = { ...newObj };

    Object.keys(newCopy).forEach((chave) => {
      const nomeEmPortugues = nomePropriedades[chave] || chave;

      if (chave === "date_end" || chave === "date_init" || chave === "date") {
        if (oldCopy[chave]) {
          oldCopy[chave] = format(parseISO(String(oldCopy[chave])), "dd/MM/yyyy");
        }
        if (newCopy[chave]) {
          newCopy[chave] = format(parseISO(String(newCopy[chave])), "dd/MM/yyyy");
        }
      }

      if (chave === "id_departamento") {
        if (oldCopy[chave]) {
          oldCopy[chave] = getNomeDepartamentoById(
            oldCopy[chave] as number | string,
          );
        }
        if (newCopy[chave]) {
          newCopy[chave] = getNomeDepartamentoById(
            newCopy[chave] as number | string,
          );
        }
      }

      if (areValuesDifferent(oldCopy[chave], newCopy[chave])) {
        mudancas[nomeEmPortugues] = {
          antigo: oldCopy[chave] || "Vazio",
          novo: newCopy[chave] || "Vazio",
          Status_Desk: "Diferentes",
        };
      }
    });
    return mudancas;
  }

  const getTextFromHtml = (text: string) =>
    text
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/\s*(p|div|li|h[1-6]|blockquote|tr)\s*>/gi, "\n")
      .replace(/<\s*li[^>]*>/gi, "- ")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const renderTextWithLinks = (text: unknown, maxLength: number = 150) => {
    if (typeof text !== "string") return null;

    const textContent = getTextFromHtml(text);
    if (textContent.length > maxLength) {
      return <ExpandableText content={textContent} maxLength={maxLength} />;
    }

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const isUrlRegex = /^https?:\/\/[^\s]+$/;

    return textContent.split("\n").map((line: string, index: number) => (
      <div key={index}>
        {line.split(urlRegex).map((part: string, i: number) =>
          isUrlRegex.test(part) ? (
            <a
              key={i}
              href={part}
              className="text-blue-500 font-thin"
              target="_blank"
              rel="noopener noreferrer"
            >
              {part}
            </a>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </div>
    ));
  };

  function getNomeDepartamentoById(id?: number | string | null) {
    const item = departamentos.find((element) => String(element.id) === String(id));
    return item ? item.nome : t("common:general");
  };

  return (
    <>
      <div className="hidden sm:flex flex-col gap-1 items-center">
        <div className="w-[2px] h-full bg-black rounded-lg" />
        <IconBullet />
        <div className="w-[2px] h-full bg-black rounded-lg" />
      </div>
      <div className="bg-cinza-100 rounded-3xl p-4 flex flex-col gap-1 text-sm mb-4 w-full">
        <div className="flex justify-between text-base">
          <b>
            {data.alteracao === "created" && t("userGoOn:timeline.note_created")}
            {data.alteracao === "update" && t("userGoOn:timeline.note_edited")}
            {data.alteracao === "delete" && t("userGoOn:timeline.note_deleted")}
          </b>
          <span className="bg-white px-3 py-px text-azul-400 rounded-2xl text-sm">
            {getNomeDepartamentoById(departamentoId)}
          </span>
        </div>
        <label className="flex items-center gap-1">
          <span>{t("userGoOn:timeline.responsible")}</span>
          <div className="flex gap-2 items-center">
            <b>
              {data?.user?.name} {data?.user?.last_name}
            </b>
            <img
              src={data?.user?.url_img}
              alt={data?.user?.name}
              className="w-6 h-6 rounded-full object-cover"
            />
          </div>
        </label>

        {data.alteracao === "update" && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4">
              {Object.keys(encontrarMudancas(data)).map((key) => (
                <p key={key} className="mb-2">
                  <span>
                    {t("userGoOn:timeline.changed")} <b>{key}</b>{" "}
                    {t("userGoOn:timeline.from")}{" "}
                  </span>
                  <span>
                    {typeof encontrarMudancas(data)[key].antigo === "object" ? (
                      <span> [Objeto]</span>
                    ) : (
                      <span className="text-gray-500">
                        {renderTextWithLinks(
                          encontrarMudancas(data)[key].antigo
                        )}
                      </span>
                    )}
                    {" " + t("userGoOn:timeline.to") + " "}
                    {typeof encontrarMudancas(data)[key].novo === "object" ? (
                      <span>[{t("userGoOn:timeline.object")}]</span>
                    ) : (
                      <span className="text-gray-800">
                        {renderTextWithLinks(encontrarMudancas(data)[key].novo)}
                      </span>
                    )}
                  </span>
                </p>
              ))}
            </div>
          </>
        )}
        {data.alteracao === "created" && data.new && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4 overflow-hidden">
              <p>
                {t("userGoOn:timeline.title")}: <br />{" "}
                <b>{parsedNew?.title}</b>
              </p>
              <p>
                {t("userGoOn:timeline.description")}: <br />{" "}
                <b>{renderTextWithLinks(parsedNew?.description)}</b>
              </p>
              {data?.nota?.file_path && (
                (() => {
                  const audioExts = ["webm", "mp3", "wav", "ogg", "m4a", "aac"];
                  const url = data?.nota?.file_path || "";
                  const fileName = url.split("/").pop() || url;
                  const extension = url.split('.').pop();
                  const isAudio = audioExts.some((ext) =>
                    (extension || "").toLowerCase().endsWith(ext),
                  );
                  return (
                    <div className="flex items-center gap-2 bg-cinza-100 rounded-xl px-3 py-2 my-2">
                      {isAudio ? (
                        <audio
                          src={url}
                          controls
                          className="flex-1 h-8"
                          style={{ accentColor: "#4A90D9" }}
                        />
                      ) : (
                        <>
                          <FileIcon size={14} className="text-cinza-500 flex-shrink-0" />
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            {fileName}
                          </a>
                        </>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          </>
        )}
        {data.alteracao === "delete" && data.nota && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4 overflow-hidden">
              <p>
                {t("userGoOn:timeline.title")}: <br /> <b>{data.nota.title}</b>
              </p>
              <p>
                {t("userGoOn:timeline.description")}: <br />{" "}
                <b>{renderTextWithLinks(data.nota.description)}</b>
              </p>
            </div>
          </>
        )}

        <span className="text-xs text-cinza-800">
          {t("userGoOn:timeline.created_at")}:{" "}
          {format(new Date(data.createdAt), "dd/MM/yyyy - HH:mm")}
        </span>
      </div>
    </>
  );
}
